import React, { useMemo, useState } from 'react';

// SVG line chart of composite G-Score over time. Handles empty/single-point
// states gracefully. Interactive: hover a point to see the exact value + date.
export default function ScoreHistoryChart({ entries }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const points = useMemo(() => {
    if (!Array.isArray(entries) || entries.length === 0) return [];
    // Sort oldest→newest defensively (backend should already do this).
    return [...entries]
      .filter((e) => e && e.takenAt != null && e.composite != null)
      .sort((a, b) => new Date(a.takenAt) - new Date(b.takenAt));
  }, [entries]);

  if (points.length === 0) {
    return (
      <EmptyState
        title="No assessments yet"
        detail="Your G-Score history will build here as you complete biweekly assessments."
      />
    );
  }

  if (points.length === 1) {
    const only = points[0];
    return (
      <EmptyState
        title="One assessment recorded"
        detail={`Your first G-Score of ${only.composite} is in — take one more assessment to see your trend line.`}
      />
    );
  }

  // Chart geometry
  const width = 640;
  const height = 260;
  const padding = { top: 20, right: 24, bottom: 32, left: 40 };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const scores = points.map((p) => p.composite);
  const yMin = Math.max(0, Math.floor(Math.min(...scores) / 10) * 10 - 10);
  const yMax = Math.min(100, Math.ceil(Math.max(...scores) / 10) * 10 + 5);
  const yRange = Math.max(1, yMax - yMin);

  const xFor = (i) =>
    padding.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const yFor = (score) => padding.top + plotH - ((score - yMin) / yRange) * plotH;

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(p.composite)}`)
    .join(' ');

  // Area under the line for a subtle fill
  const areaD =
    `${pathD} L ${xFor(points.length - 1)} ${padding.top + plotH}` +
    ` L ${xFor(0)} ${padding.top + plotH} Z`;

  // Y-axis reference lines every 20 points
  const yTicks = [];
  for (let v = Math.ceil(yMin / 20) * 20; v <= yMax; v += 20) yTicks.push(v);

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label="G-Score over time"
      >
        {/* Y-axis reference lines */}
        {yTicks.map((v) => (
          <g key={v}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yFor(v)}
              y2={yFor(v)}
              stroke="var(--border-subtle)"
              strokeWidth={1}
            />
            <text
              x={padding.left - 8}
              y={yFor(v)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="10"
              fill="var(--text-tertiary)"
            >
              {v}
            </text>
          </g>
        ))}

        {/* Area fill */}
        <path d={areaD} fill="var(--accent-light)" opacity={0.6} />

        {/* Line */}
        <path
          d={pathD}
          fill="none"
          stroke="var(--accent-primary)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Points + invisible hover hitboxes */}
        {points.map((p, i) => (
          <g key={p.runId || i}>
            <circle
              cx={xFor(i)}
              cy={yFor(p.composite)}
              r={hoverIndex === i ? 5 : 3.5}
              fill="var(--accent-primary)"
              stroke="var(--bg-secondary)"
              strokeWidth={2}
              style={{ transition: 'r var(--transition-fast)' }}
            />
            <rect
              x={xFor(i) - 14}
              y={padding.top}
              width={28}
              height={plotH}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex(null)}
              onFocus={() => setHoverIndex(i)}
              onBlur={() => setHoverIndex(null)}
              tabIndex={0}
              aria-label={`${formatDate(p.takenAt)}: G-Score ${p.composite}`}
            />
          </g>
        ))}

        {/* X-axis endpoints */}
        <text
          x={xFor(0)}
          y={height - 10}
          textAnchor="middle"
          fontSize="10"
          fill="var(--text-tertiary)"
        >
          {formatDateShort(points[0].takenAt)}
        </text>
        <text
          x={xFor(points.length - 1)}
          y={height - 10}
          textAnchor="middle"
          fontSize="10"
          fill="var(--text-tertiary)"
        >
          {formatDateShort(points[points.length - 1].takenAt)}
        </text>
      </svg>

      {/* Tooltip */}
      {hoverIndex != null && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `${(xFor(hoverIndex) / width) * 100}%`,
            transform: 'translate(-50%, -100%)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-control)',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-numeric-size)',
            color: 'var(--text-primary)',
            boxShadow: 'var(--shadow-md)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 'var(--text-body-size)' }}>
            {points[hoverIndex].composite}
          </div>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-label-size)', marginTop: 'var(--space-1)' }}>
            {formatDate(points[hoverIndex].takenAt)}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ title, detail }) {
  return (
    <div
      style={{
        background: 'var(--bg-tertiary)',
        border: '1px dashed var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-6) var(--space-5)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 'var(--text-body-size)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-2)',
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-tertiary)', maxWidth: 320, margin: '0 auto' }}>
        {detail}
      </div>
    </div>
  );
}

function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
